import { init, setUserId, track } from "@amplitude/analytics-browser";
import jwtDecode from "jwt-decode";
import { useAuthStore } from "./store";
import { trackGaEvent } from "./googleAnalytics";

export const amplitudeInit = () => {
  init(process.env.REACT_APP_AMPLITUDE_API ?? "", undefined, {});
};

export const trackEvent = (
  eventName: string,
  properties?: Map<string, any>
) => {
  track(eventName, properties);
};

export function handleClickForTracking(event: any) {
  const sourceElement = event.target as HTMLElement | null;
  if (!sourceElement) return;

  const clickedElement = sourceElement.closest<HTMLElement>(
    "button, a, input, select, textarea, [role='button'], [role='menuitem'], [role='tab']"
  );
  const trackedElement = clickedElement ?? sourceElement;
  const elementLabel = getElementLabel(trackedElement);

  trackGaEvent("ui_click", {
    page_path: window.location.pathname,
    element_type: trackedElement.tagName.toLowerCase(),
    interaction_type: getInteractionType(trackedElement),
    // Grid and table rows can contain employee or company names. Track the
    // interaction, but not the visible label from those data-heavy areas.
    ...(isSafeElementLabel(trackedElement, elementLabel)
      ? { element_label: elementLabel }
      : {}),
  });

  // Preserve the existing Amplitude button/link tracking behaviour. GA4's
  // ui_click event above deliberately captures all clicks across the app.
  const amplitudeElement = sourceElement.closest<HTMLElement>("button, a");
  if (amplitudeElement) {
    const actionName = getElementLabel(amplitudeElement);
    const screen = window.location.pathname;

    const eventName = `${screen} ${actionName}`;
    const accesstoken = useAuthStore.getState().accessToken;
    if (accesstoken) {
      let { emailId } = decodeAuthToken(accesstoken);
      emailId = emailId?.padStart(10, "_");
      setUserId(emailId);
    }
    track(eventName, undefined, {});
  }
}

function getElementLabel(element: HTMLElement) {
  return (
    element.getAttribute("data-event-name") ||
    element.getAttribute("aria-label") ||
    element.innerText?.trim()
  );
}

function getInteractionType(element: HTMLElement) {
  if (element.matches("button, [role='button']")) return "button";
  if (element.matches("a")) return "link";
  if (element.matches("input")) return "input";
  if (element.matches("select")) return "select";
  if (element.matches("textarea")) return "textarea";
  if (element.matches("[role='menuitem']")) return "menu_item";
  if (element.matches("[role='tab']")) return "tab";
  return "other";
}

function isSafeElementLabel(element: HTMLElement, label?: string) {
  if (!label || label.length > 80) return false;
  if (/\S+@\S+\.\S+/.test(label) || /\d{4,}/.test(label)) return false;

  return !element.closest(
    ".ag-root, .ag-row, [role='row'], table, [role='grid'], [role='treegrid']"
  );
}

export function decodeAuthToken(accesstoken: string) {
  const decodedToken = jwtDecode(accesstoken) as {
    id: string;
    emailId: string;
  };
  return decodedToken;
}
